import { useEffect } from 'react'
import { ChevronLeft, ChevronRight, Clock, DeviceIconSvg, Lock, X } from '../../icons'
import type { Device, LastChange } from '../../types/device'
import { PowerButton } from '../PowerButton'
import { PanelShell } from './PanelShell'
import { useSchedules } from '../../hooks/useSchedules'

const DAYS_IT = ['LUN', 'MAR', 'MER', 'GIO', 'VEN', 'SAB', 'DOM'] as const
const DAYS_KEY = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const

function formatTime(at: number): string {
  const d = new Date(at)
  const today = new Date()
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  if (sameDay) return `${hh}:${mm}`
  return `${d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })} ${hh}:${mm}`
}

function formatLastChange(lc: LastChange): string {
  const when = typeof lc.at === 'number' ? ` · ${formatTime(lc.at)}` : ''
  switch (lc.by) {
    case 'act-user':       return `${lc.name ?? 'Utente ACT'}${when}`
    case 'schedule':       return `Programmazione automatica${when}`
    case 'wall-switch':    return `Interruttore a parete${when}`
    case 'shelly-cloud':   return `Shelly Cloud (super admin)${when}`
    case 'unknown':        return `Origine non rilevata${when}`
  }
}

function lastChangeTooltip(lc: LastChange): string {
  switch (lc.by) {
    case 'act-user':       return 'Modifica eseguita da un utente loggato in ACT.'
    case 'schedule':       return 'Cambio innescato da una fascia oraria programmata sul dispositivo.'
    case 'wall-switch':    return 'Comando arrivato dall\'interruttore a parete (input fisico del dispositivo).'
    case 'shelly-cloud':   return 'Cambio arrivato dal canale cloud di Shelly: o un super admin OWW dall\'app Shelly/pannello web, oppure una regola di automazione del backend Sintropy.'
    case 'unknown':        return 'Origine non rilevata o cambio avvenuto prima dell\'avvio del server.'
  }
}

interface Props {
  device: Device
  isAdmin: boolean
  onClose: () => void
  onBack: (() => void) | null
  onToggle: (id: string) => void
  onOpenSchedule: (device: Device) => void
  showClose: boolean
  asSheet: boolean
}

export function SwitchDetails({ device, isAdmin, onClose, onBack, onToggle, onOpenSchedule, showClose, asSheet }: Props) {
  const lockedForUser = !!device.isProtected && !isAdmin
  const state = !device.online ? 'offline' : lockedForUser ? 'locked' : device.relay?.on ? 'on' : 'off'
  // Skip the schedules fetch entirely for locked-out users — the server would
  // 403 anyway, and there's nothing to render.
  const { data: sched } = useSchedules(device.id, device.online && !lockedForUser)
  const bands = sched?.bands ?? []
  const hasSchedule = bands.length > 0

  useEffect(() => {
    if (!showClose) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, showClose])

  const body = (
    <div className="dpanel" data-state={state}>
      {onBack && (
        <button className="dpanel__back" type="button" onClick={onBack}>
          <ChevronLeft />
          <span>Panoramica sito</span>
        </button>
      )}
      <div className="dpanel__header">
        <div className="dpanel__title-wrap">
          <div className="dpanel__icon">
            <DeviceIconSvg name={device.icon} />
          </div>
          <div>
            <div className="dpanel__name">{device.name}</div>
            <div className="dpanel__id">{device.id}</div>
          </div>
        </div>
        {showClose && (
          <button className="modal__close" onClick={onClose} aria-label="Chiudi">
            <X />
          </button>
        )}
      </div>

      <div className="dpanel__control-row">
        <PowerButton
          on={!!device.relay?.on && device.online}
          disabled={!device.online || lockedForUser}
          locked={lockedForUser}
          size={64}
          onClick={() => onToggle(device.id)}
        />
        <div className="dpanel__control-text">
          <div className="dpanel__control-label">
            {!device.online
              ? 'Offline'
              : lockedForUser
                ? 'Riservato'
                : device.relay?.on ? 'Acceso' : 'Spento'}
          </div>
          <div className="dpanel__control-hint">
            {!device.online
              ? 'Dispositivo non raggiungibile'
              : lockedForUser
                ? 'Operazione consentita solo agli amministratori'
                : device.relay?.on ? 'Tocca per spegnere' : 'Tocca per accendere'}
          </div>
        </div>
      </div>

      {device.lastChange && !lockedForUser && (
        <div className="dpanel__last-change" title={lastChangeTooltip(device.lastChange)}>
          <span className="dpanel__last-change-k">Ultimo cambio:</span>
          <span className="dpanel__last-change-v">{formatLastChange(device.lastChange)}</span>
        </div>
      )}

      <div className="dpanel__tag-row">
        <span className="dpanel__tag">Interruttore</span>
        {device.kind === 'switch' ? (
          <span className="dpanel__tag dpanel__tag--muted">Senza misurazione</span>
        ) : (
          <span className="dpanel__tag dpanel__tag--muted">Con misurazione</span>
        )}
        {device.isProtected && (
          <span className="dpanel__tag dpanel__tag--lock" title="Riservato agli amministratori">
            <Lock />
            <span>Riservato amministratori</span>
          </span>
        )}
      </div>

      <div className="dpanel__section-label dpanel__section-label--inline">
        <span>Programmazione</span>
        {hasSchedule && (
          <span className="count-pill count-pill--lime">
            {bands.length === 1 ? '1 fascia' : `${bands.length} fasce`}
          </span>
        )}
      </div>

      {lockedForUser ? (
        <div className="sched-empty">
          <div className="sched-empty__title">Non visibile</div>
          <div className="sched-empty__sub">La programmazione di questo dispositivo è consultabile solo dagli amministratori.</div>
        </div>
      ) : hasSchedule ? (
        <div className="sched-bands">
          {bands.map((band, i) => (
            <div className="sched-band" key={`${band.onJobId ?? i}-${band.offJobId ?? i}`}>
              <div className="sched-band__times">
                <span className="sched-band__t">{band.onAt}</span>
                <span className="sched-band__arrow"><ChevronRight /></span>
                <span className="sched-band__t">{band.offAt}</span>
              </div>
              <div className="sched-band__days">
                {DAYS_IT.map((label, j) => {
                  const key = DAYS_KEY[j]
                  const on = band.days.includes(key)
                  return <span key={label} className={`sched-preview__day${on ? ' on' : ''}`}>{label}</span>
                })}
              </div>
            </div>
          ))}
          <div className="sched-preview__hint">
            La programmazione viene eseguita direttamente sul dispositivo.
          </div>
        </div>
      ) : (
        <div className="sched-empty">
          <div className="sched-empty__title">Nessuna programmazione</div>
          <div className="sched-empty__sub">Imposta uno o più intervalli orari di accensione/spegnimento automatici.</div>
        </div>
      )}

      <div className="dpanel__actions">
        <button
          className="btn btn--ghost"
          disabled={!device.online || lockedForUser}
          onClick={() => onOpenSchedule(device)}
          title={lockedForUser ? 'Riservato amministratori' : undefined}
        >
          <Clock />
          <span>{hasSchedule ? 'Modifica programmazione' : 'Imposta programmazione'}</span>
        </button>
      </div>

      <div className="dpanel__footer">
        <div className="dpanel__footer-k">Modello</div>
        <div className="dpanel__footer-v">{device.model || `Shelly Gen ${device.gen ?? '?'}`}</div>
        <div className="dpanel__footer-k">Ultima lettura</div>
        <div className="dpanel__footer-v">pochi secondi fa</div>
      </div>
    </div>
  )

  return <PanelShell asSheet={asSheet} onClose={onClose}>{body}</PanelShell>
}
